import { Box } from '@deckee/deck-hand';
import type {
  BoatingRiskRating,
  HourlyMarineConditions,
  VesselProfile,
  WaterBody,
} from '@deckee/utilities';
import React, { FC } from 'react';
import styled from 'styled-components';

export interface RiskForecastDebugPanelProps {
  conditions: HourlyMarineConditions;
  vessel: VesselProfile;
  waterBody: WaterBody;
  rating: BoatingRiskRating;
  /** Selected hour timestamp, for context when scrubbing. */
  selectedTime?: string;
}

interface DebugRow {
  label: string;
  value: string;
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.45;
`;

const Cell = styled.td`
  padding: 2px 0;
  vertical-align: top;
  color: ${({ theme }) => theme.colors.darkergrey};

  &:first-child {
    padding-right: 12px;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.mediumgrey};
  }
`;

const formatNumber = (value: number, digits = 2): string =>
  Number.isFinite(value) ? value.toFixed(digits) : String(value);

/**
 * Alpha-only table of the inputs that produced the selected hour's risk rating.
 * Lives inside the Boating forecast card so scrubbing updates it live.
 */
const RiskForecastDebugPanel: FC<RiskForecastDebugPanelProps> = ({
  conditions,
  vessel,
  waterBody,
  rating,
  selectedTime,
}) => {
  const rows: DebugRow[] = [
    { label: 'rating', value: rating },
    { label: 'waterBody', value: waterBody },
    { label: 'selectedTime', value: selectedTime ?? '—' },
    { label: 'vessel.type', value: vessel.type },
    {
      label: 'vessel.lengthM',
      value: formatNumber(vessel.lengthM, 1),
    },
    { label: 'vessel.graphic', value: vessel.graphic },
    {
      label: 'vessel.windAmberKn',
      value: formatNumber(vessel.windThresholdsKn.amber, 1),
    },
    {
      label: 'vessel.windRedKn',
      value: formatNumber(vessel.windThresholdsKn.red, 1),
    },
    {
      label: 'vessel.seaAmberM',
      value: formatNumber(vessel.seaBandM.amber, 2),
    },
    {
      label: 'vessel.seaRedM',
      value: formatNumber(vessel.seaBandM.red, 2),
    },
    {
      label: 'vessel.compounding',
      value: formatNumber(vessel.compoundingFactor, 2),
    },
    {
      label: 'swellHeightM (hs)',
      value: formatNumber(conditions.swellHeightM, 2),
    },
    {
      label: 'swellPeriodS (tp)',
      value: formatNumber(conditions.swellPeriodS, 1),
    },
    {
      label: 'windSpeedMps (ws)',
      value: formatNumber(conditions.windSpeedMps, 2),
    },
    {
      label: 'windDirectionDeg (wd)',
      value: formatNumber(conditions.windDirectionDeg, 0),
    },
    {
      label: 'swellDirectionDeg (sd)',
      value: formatNumber(conditions.swellDirectionDeg, 0),
    },
  ];

  return (
    <Box
      mt={3}
      p={2}
      width="100%"
      bg="lightgrey"
      borderRadius="5px"
      data-testid="risk-forecast-debug-panel"
    >
      <Box fontSize={1} fontWeight="bold" color="darkergrey" mb={2}>
        Risk forecast inputs
      </Box>
      <Table>
        <tbody>
          {rows.map(({ label, value }) => (
            <tr key={label}>
              <Cell>{label}</Cell>
              <Cell>{value}</Cell>
            </tr>
          ))}
        </tbody>
      </Table>
    </Box>
  );
};

export default RiskForecastDebugPanel;
